import {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  GetStaticPathsResult,
  GetStaticPropsContext,
  GetStaticPropsResult,
} from 'next'
import { ApiError } from '@lib/api/errors'

export const isServer = () => typeof window === 'undefined'

export class SSGRedirectError extends Error {
  path: string

  constructor(path: string, message: string) {
    super(message)
    this.path = path
  }
}

async function withErrorHandling<T extends (...args: any[]) => any>(
  callback: T,
  options?: { notFoundRevalidate?: number }
) {
  try {
    return await callback()
  } catch (err: any) {
    if (err instanceof SSGRedirectError) {
      return {
        redirect: {
          statusCode: 302,
          destination: err.path,
        },
        revalidate: 10,
      }
    }
    // Only return notFound for actual 404s - this caches the result
    if (err instanceof ApiError && err.status === 404) {
      const notFoundResult: { notFound: true; revalidate?: number } = {
        notFound: true,
      }
      if (typeof options?.notFoundRevalidate === 'number') {
        notFoundResult.revalidate = options.notFoundRevalidate
      }
      return {
        ...notFoundResult,
      }
    }
    // For all other API errors, log and rethrow
    // This prevents caching the error as a 404 and allows retries
    if (err instanceof ApiError) {
      console.error('API Error:', err.status, err.message)
    } else {
      console.error('SSR Error:', err)
    }
    // Rethrow the error so Next.js doesn't cache it
    // For SSG with fallback: 'blocking', this will retry on next request
    // For SSR, this will show the Next.js error page
    throw err
  }
}

export function ssrProps<P = Record<string, any>>(
  getProps: (ctx: GetServerSidePropsContext) => Promise<P> | P
): (ctx: GetServerSidePropsContext) => Promise<GetServerSidePropsResult<P>> {
  return async (ctx: GetServerSidePropsContext) =>
    withErrorHandling(async () => {
      const props = await getProps(ctx)
      return {
        props,
      }
    })
}

export function ssgProps<P = Record<string, any>>(
  getProps: (ctx: GetStaticPropsContext) => Promise<P> | P
): (ctx: GetStaticPropsContext) => Promise<GetStaticPropsResult<P>> {
  return async (ctx: GetStaticPropsContext) =>
    withErrorHandling(
      async () => {
        const props = await getProps(ctx)
        return {
          props,
          revalidate: 10,
        }
      },
      { notFoundRevalidate: 10 }
    )
}

export function ssgPaths(paths?: GetStaticPathsResult['paths']) {
  return () => {
    return {
      paths: paths ?? [],
      fallback: 'blocking',
    }
  }
}
